import * as React from 'react';
import {
  MdAdd,
  MdCheckCircle,
  MdPauseCircle,
  MdSupportAgent,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../Components/Table/DataGrid';
import DeleteModel from '../../../Components/Common/DeleteModel';
import { getAllAegisAiAssistConfigs, deleteAegisAiAssistConfig, getAegisAiAssistIconUrl } from '../../../Services/AegisAiAssistService';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../../Utils/Permissions';
import { APPRoutes } from '../../../Utils/Route';
import { getAssistIconComponent } from '../../../Utils/AssistIcons';
import { toast } from 'react-toastify';
import PageTitle from '../../../Components/Common/PageTitle';
import SearchableDropdown from '../../../Components/Subscription/SearchableDropdown';
import { AiRowActions, AiSearchBox, AiStatusPill } from '../AegisAiUi';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({ value: option, label: option }));
const STATUS_TONE = {
  Active: 'success',
  Inactive: 'inactive',
};

export default function AegisAiAssistList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const canViewConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_VIEW, decodedToken);
  const canCreateConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_CREATE, decodedToken);
  const canEditConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_EDIT, decodedToken);
  const canDeleteConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_DELETE, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const loadConfigs = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllAegisAiAssistConfigs(token);
      if (response?.is_success) {
        const configs = Array.isArray(response?.data?.AssistConfigs) ? response.data.AssistConfigs : [];
        setRows(configs);
        setSummary(response?.data || null);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal server error. Please Contact Support Team.');
        setRows([]);
        setSummary(null);
      } else {
        toast.error(response?.message || 'Failed to load assist configs');
        setRows([]);
        setSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadConfigs();
    }
  }, [token, loadConfigs]);

  const handleCreate = React.useCallback(() => {
    navigate(APPRoutes.AEGIS_AI_ASSIST_FORM);
  }, [navigate]);

  const handleView = React.useCallback((row) => {
    navigate(APPRoutes.AEGIS_AI_ASSIST_VIEW.replace(':id', row.Id), { state: { assistConfig: row } });
  }, [navigate]);

  const handleEdit = React.useCallback((row) => {
    navigate(APPRoutes.AEGIS_AI_ASSIST_EDIT.replace(':id', row.Id));
  }, [navigate]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) return;
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteAegisAiAssistConfig(deleteTarget.Id, token);
      if (response?.is_success) {
        setRows((prev) => prev.filter((cfg) => cfg.Id !== deleteTarget.Id));
        setDeleteTarget(null);
        loadConfigs();
        toast.success(response?.message || 'Assist config deleted successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete assist config.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the assist config.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadConfigs]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const rowStatus = row.Status || '';
      const matchesStatus = status === 'All' || rowStatus === status;
      const matchesSearch =
        !term ||
        row.Title?.toLowerCase().includes(term) ||
        row.ClientName?.toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    const total = summary?.TotalAssistConfigs ?? rows.length;
    const active = summary?.ActiveAssistConfigs ?? 0;
    const inactive = summary?.InactiveAssistConfigs ?? 0;

    return [
      {
        label: 'Total Widgets',
        value: total,
        icon: <MdSupportAgent size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active',
        value: active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive',
        value: inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ];
  }, [rows, summary]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'Title',
        label: 'Widget',
        minWidth: 60,
        format: (value, row) => {
          const Icon = getAssistIconComponent(row.Icon, getAegisAiAssistIconUrl(row.Id, row.IconFileId));
          return (
            <div className="hc-ai-cell d-flex align-items-center">
              <span
                className="hc-ai-cell-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 position-relative hc-ai-cell-icon-solid"
                style={{ backgroundColor: row.PrimaryColor || 'var(--hc-gradient-mid)' }}
              >
                <Icon size={18} />
              </span>
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span className="hc-ai-cell-title fw-bold">{value}</span>
                {row.Description ? <span className="hc-ai-cell-sub overflow-hidden" title={row.Description}>{row.Description}</span> : null}
              </div>
            </div>
          );
        },
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'ClientName',
        label: 'Client',
        minWidth: 100,
        format: (value) => value || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'Status',
        label: 'Status',
        minWidth: 50,
        align: 'center',
        format: (value) => <AiStatusPill tone={STATUS_TONE[value] || 'inactive'} label={value} />,
      },
      {
        id: 'CreatedBy',
        label: 'Created By',
        minWidth: 70,
      },
    );

    if (canViewConfig || canEditConfig || canDeleteConfig) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 80,
        align: 'center',
        format: (_value, row) => (
          <AiRowActions
            onView={canViewConfig ? () => handleView(row) : undefined}
            onEdit={canEditConfig ? () => handleEdit(row) : undefined}
            onDelete={canDeleteConfig ? () => handleDeleteClick(row) : undefined}
          />
        ),
      });
    }

    return cols;
  }, [isSuperAdmin, handleView, handleEdit, handleDeleteClick, canViewConfig, canEditConfig, canDeleteConfig]);

  return (
    <>
      <PageTitle title="Aegis AI Agent Builder - Aegis 360" />
      <div className="hc-ai-page">
        <div className="d-flex flex-md-row flex-column align-items-start align-items-md-center justify-content-between gap-2 mb-4">
          <div>
            <h4 className="fw-bold mb-1 hc-text-primary fs-3">Aegis AI Agent Builder</h4>
            <p className="text-secondary mb-0 small">
              Configure branded, embeddable chat widgets clients can add to their own website
            </p>
          </div>
          {canCreateConfig ? (
            <button onClick={handleCreate} type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary">
              <MdAdd size={20} />
              <span>Create Widget</span>
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="row g-3 mb-3">
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="col-12 col-sm-6 col-lg-4">
                <div className="bg-white p-4 rounded-4 shadow">
                  <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-3 mb-3">
            {stats.map((stat) => (
              <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
                <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                  <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                  <div className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}>
                    {stat.icon}
                  </div>
                  <div className="d-flex flex-column fw-bold lh-sm">
                    <span className="text-dark" style={{ fontSize: '0.85rem' }}>{stat.label}</span>
                    <span className={`fw-bold fs-5 ${stat.valueClass}`}>{stat.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="hc-ai-panel bg-white">
          <div className="hc-ai-panel-head d-flex flex-wrap align-items-center justify-content-between mb-3">
            <h2 className="hc-ai-panel-title d-flex align-items-center m-0">
              <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 10 }}>
                <MdSupportAgent size={18} />
              </span>
              Widgets
            </h2>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-12 col-md-8 col-lg-9">
              <AiSearchBox value={search} onChange={setSearch} placeholder="Search Widget By Title" />
            </div>
            <div className="col-12 col-md-4 col-lg-3 hc-ai-filter">
              <SearchableDropdown placeholder="Select status" options={STATUS_DROPDOWN_OPTIONS} value={status} onChange={setStatus} showSearch={true} />
            </div>
          </div>

          <div className="hc-ai-grid-wrap bg-white">
            <DataGrid
              columns={columns}
              rows={filteredRows}
              loading={loading}
              skeletonRows={6}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          </div>
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Widget"
        entityLabel="widget"
        itemName={deleteTarget?.Title || ''}
        description="You are about to remove this widget configuration. Any embedded script using it will stop working."
        confirmText="Delete Widget"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
